<!--
  SupplierBoxDetailPage — the DEDICATED per-order detail route
  (/suppliers/box/:boxId/) of the Suppliers-Club Daily Orders feature.
  Box-first design spec:
  _bmad-output/planning-artifacts/supplier-daily-orders-boxfirst-design-spec-2026-07-07.md Section 3/Section 4/Section 7/Section 8

  Reached by tapping an order card on /suppliers/orders/ (SupplierDailyOrdersPage).
  Lists ONE box's items and hosts the invoice + source-post sheets. Read-first
  (owner, 30 Oct 2026 — design supplier-daily-box-read-first-design-2026-10-30.md):
  an open day is one list with the two OPTIONAL taps «معايا» / «نواقص» and no
  "answered x of y" bar; a finished day (RECEIVED/CLOSED) splits its lines into
  what the collector took, then what he did not (`collected`, the plan outcome).

  DATA is owned by useSupplierBoxDetail(boxId) (called ONCE here): one lazy
  GET /supplier-boxes/{id}?include=items,items.notes,invoice, the advisory writes
  (reserve / markOos / clear), and the SINGLE-box IN_COLLECTION poll (self-arms
  via the composable's pollArmed watch; disarms on any other lifecycle state).

  ROUTE INVARIANTS (playbook Section 5, design spec Section 3):
   - EAGER `component:` in routes.js — a push / PWA cold-launch can make this URL
     the boot page; `asyncComponent:` would paint "[object Promise]" (row 16).
   - Guarded by requireSupplier; an unauthenticated cold hard-load is bounced to
     /login/ by App.vue's EAGER_AUTH_REQUIRED_PREFIXES pre-empt BEFORE this mounts
     (row 22 — eager component rendering an <f7-page> skips beforeEnter on the
     initial URL).
   - `meta.hideTabbar: true` — pushed over the shell, focus on the order.

  TEARDOWN (composable H1 caveat): useSupplierBoxDetail auto-disposes its poll +
  visibilitychange listener via onScopeDispose, but framework7-vue short-circuits
  Vue unmount for keepAlive pages, so we ALSO call dispose() on @page:beforeremove
  as a belt-and-braces F7-native teardown (mirrors OrderBoxTotalsPage). This route
  is NOT keepAlive; dispose() is idempotent, so the double-call is safe.

  Per-order summary bar (design spec Section 2/Section 3, "fixed header, NOT part of the scroll"):
  a `position:absolute` bar pinned below the navbar (top: var(--f7-page-navbar-offset)),
  its height reserved as --f7-page-content-extra-padding-top via the SHARED
  useFixedSummaryBar composable — byte-identical wiring to the list page's overview strip
  so both pages render the summary the same way. It lives outside the page-content
  scroller, so it stays put; page-content spans the full page behind it, so
  hide-navbar-on-scroll reveals scrolled content (no blank strip).

  NOT a normal-flow `flex-shrink:0` sibling: `.page` is `position:absolute; display:block`
  (NOT a flex container — verified in framework7-bundle.css + the F7-Vue page-content=false
  render), so flex-shrink is inert and a `height:100%` page-content sibling overflows the
  page bottom by the bar's height, clipping the last item (P5 live verification 2026-07-07;
  see useFixedSummaryBar.js).
-->
<template>
  <f7-page
    name="supplier-box-detail"
    :page-content="false"
    class="supplier-box-detail-page"
    :style="pageStyleVars"
    @page:beforeremove="onPageBeforeRemove"
  >
    <!-- `back-link-show-text` is required for the «رجوع» label to render at
         all: F7's nav-left drops the label unless it is set, leaving a bare
         chevron (nav-left.js — `backLink !== true && backLinkShowText`). -->
    <f7-navbar :back-link="$t('general.back')" back-link-show-text>
      <f7-nav-title :subtitle="dateSubline">{{ dayTitle }}</f7-nav-title>
    </f7-navbar>

    <!-- Per-order summary bar — fixed header pinned below the navbar, outside the scroll
         (design spec Section 2/Section 3), matching the list page's overview strip. Positioned absolute
         + height reserved via useFixedSummaryBar. Money hero (invoiceable → caption flips
         on lifecycle). Only once box loads. -->
    <div v-if="box" :ref="setSummaryEl" class="sbd-summary">
      <div class="sbd-summary__money">
        <span class="sbd-summary__amt">{{ formattedMoney }}</span>
        <span class="sbd-summary__cap">{{ moneyCaptionText }}</span>
      </div>
    </div>

    <f7-page-content :ptr="!replayOn" :ptr-mousewheel="!replayOn" @ptr:refresh="onPtrRefresh">
      <!-- Load failed with nothing cached — distinct retry state, never a
           misleading empty list. -->
      <f7-block v-if="errorState" class="text-align-center sbd-error">
        <f7-icon f7="wifi_exclamationmark" size="48" color="orange"></f7-icon>
        <p>{{ error }}</p>
        <f7-button large outline class="sbd-error__retry" @click="onRetry">
          {{ $t('supplier.dailyOrders.boxList.error.retry') }}
        </f7-button>
      </f7-block>

      <!-- First-load skeleton (item-card-shaped) — only while nothing is cached. -->
      <div v-else-if="firstLoadSkeleton" class="sbd-skeleton" aria-hidden="true">
        <div v-for="n in 3" :key="n" class="sbd-skel-card">
          <f7-skeleton-block
            class="sbd-skel-card__line sbd-skel-card__line--title skeleton-effect-wave"
          ></f7-skeleton-block>
          <f7-skeleton-block
            class="sbd-skel-card__line sbd-skel-card__line--sub skeleton-effect-wave"
          ></f7-skeleton-block>
          <f7-skeleton-block
            class="sbd-skel-card__line sbd-skel-card__line--actions skeleton-effect-wave"
          ></f7-skeleton-block>
        </div>
      </div>

      <!-- Empty — shouldn't occur (you only reach a detail via a card that has
           items) but kept as a graceful fallback (design spec Section 8). -->
      <f7-block v-else-if="emptyState" class="text-align-center sbd-empty">
        <f7-icon f7="tray" size="72" color="gray"></f7-icon>
        <h3>{{ $t('supplier.dailyOrders.empty.title') }}</h3>
        <p>{{ $t('supplier.dailyOrders.empty.description') }}</p>
      </f7-block>

      <template v-else>
        <!-- Leading invoice card (v-if closed + hasInvoice) — invoice-placement
             Candidate B: the closed order's invoice affordance leads the scroll.
             @click opens the invoice sheet (fetches a fresh 5-min signed URL). -->
        <button
          v-if="showInvoiceCard"
          type="button"
          class="sbd-invoice-card"
          :aria-label="invoiceAria"
          @click="openInvoice"
        >
          <span class="sbd-invoice-card__icon">
            <f7-icon f7="doc_text_fill" size="22"></f7-icon>
          </span>
          <span class="sbd-invoice-card__label">
            {{ $t('supplier.dailyOrders.invoice.sheetTitle') }}
          </span>
          <span class="sbd-invoice-card__cta">
            {{ $t('supplier.dailyOrders.invoice.chipLabel') }}
          </span>
        </button>

        <!-- The order's items — the ACCEPTED v2 item card, unchanged. -->
        <supplier-locked-notice v-if="isShowroom"></supplier-locked-notice>

        <!-- One list while the day is open; taken / not taken once it is
             finished, each under its heading (an empty group is not drawn). -->
        <template v-for="group in itemGroups" :key="group.key">
          <div v-if="group.heading" class="sbd-group-heading">{{ group.heading }}</div>
          <supplier-daily-order-item-card
            v-for="item in group.items"
            :key="item.id"
            :item="item"
            :answers-locked="isShowroom"
            @reserve="reserve"
            @oos="markOos"
            @clear="clear"
            @view-post="onViewPost"
          ></supplier-daily-order-item-card>
        </template>
      </template>
    </f7-page-content>

    <!-- Source-post + photos sheet (reused, one-per-item-tapped singleton). -->
    <supplier-source-post-sheet
      v-model:opened="sourceSheetOpen"
      :item="activeSourceItem"
    ></supplier-source-post-sheet>

    <!-- Invoice sheet — fetches fresh at open (5-min signed documentUrl). -->
    <supplier-invoice-sheet
      v-model:opened="invoiceSheetOpen"
      :box-id="boxId"
    ></supplier-invoice-sheet>
  </f7-page>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { isReplayOn } from '@/collector/previewReplay.js';
import SupplierDailyOrderItemCard from '@/components/supplier/SupplierDailyOrderItemCard.vue';
import SupplierInvoiceSheet from '@/components/supplier/SupplierInvoiceSheet.vue';
import SupplierLockedNotice from '@/components/supplier/SupplierLockedNotice.vue';
import SupplierSourcePostSheet from '@/components/supplier/SupplierSourcePostSheet.vue';
import { useCurrency } from '@/composables/useCurrency.js';
import { useFixedSummaryBar } from '@/composables/useFixedSummaryBar.js';
import { useSupplierBoxDetail } from '@/composables/useSupplierDailyOrders.js';
import { SupplierBoxState } from '@/enums/commerce/SupplierSignal.enum.js';
import { useAuthStore } from '@/stores/authStore.js';

defineOptions({
  name: 'SupplierBoxDetailPage',
});

// F7-Vue passes f7route / f7router as props to `component:` routes (same as
// OrderBoxTotalsPage). boxId is read from the route param.
const props = defineProps({
  f7route: { type: Object, required: true },
  f7router: { type: Object, default: null },
});

// An offline supplier preview (supplier-preview-offline L-J): the phone's copy
// has nothing to refresh from.
const replayOn = isReplayOn();

const { t, locale } = useI18n();
const { formatCurrency } = useCurrency();

// Showroom stage reads his day but cannot answer (the server 403s the writes).
const isShowroom = computed(() => useAuthStore().supplierStage === 'showroom');

// Fixed per-order summary bar — pins below the navbar (outside the scroll) and reserves
// its height as page-content padding so the last item is never clipped (P5, 2026-07-07).
const { setSummaryEl, pageStyleVars } = useFixedSummaryBar();

// A getter (not a plain read) so the composable's toValue() resolves the param
// freshly; the detail route mounts a fresh instance per box, so this is stable.
const boxId = computed(() => props.f7route?.params?.boxId ?? null);

// The ONE detail data layer for this box — items + advisory writes + the
// single-box IN_COLLECTION poll (self-arms via the composable's pollArmed watch).
const {
  box,
  items,
  loading,
  error,
  loadInitial,
  refresh,
  reserve,
  markOos,
  clear,
  dispose,
} = useSupplierBoxDetail(boxId);

// ── Lifecycle derivation — mirrors SupplierBox.js / SupplierBoxCard.vue exactly
// (Number(lifecycleState) >= RECEIVED) rather than reading the model getter, so a
// plain object (dev fixture / test) renders identically to a pinia-orm instance. ─
const isClosed = computed(() => Number(box.value?.lifecycleState) >= SupplierBoxState.RECEIVED);
// Open → estimate; closed with an invoice → invoice total; closed with no
// invoice → 'bought' (collected lines only — BE brief 2026-10-19-supplier-box-
// finished-day-money.md).
const moneyCaptionKey = computed(() => {
  if (!isClosed.value) return 'open';
  return box.value?.hasInvoice ? 'closed' : 'bought';
});
const showInvoiceCard = computed(() => isClosed.value && Boolean(box.value?.hasInvoice));

// ── Title — the box's collectionDate rendered relative-then-dated. Mirrors
// useSupplierDailyOrders' dayLabelFor + SupplierBoxCard's local copy (same
// tz-agnostic local-Y-M-D comparison, same i18n keys); kept local because
// dayLabelFor is not an exported symbol. ─────────────────────────────────────
function localYmd(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function collectionYmd(collectionDate) {
  if (!collectionDate) return null;
  const ymd = String(collectionDate).slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return ymd;
  const parsed = new Date(collectionDate);
  if (Number.isNaN(parsed.getTime())) return null;
  return localYmd(parsed);
}

const dayTitle = computed(() => {
  const ymd = collectionYmd(box.value?.collectionDate);
  if (!ymd) return t('supplier.dailyOrders.dayLabel');
  if (ymd === localYmd(new Date())) return t('supplier.dailyOrders.dayLabel');
  const [, mm, dd] = ymd.split('-');
  return t('supplier.dailyOrders.dayLabelDated', { date: `${Number(dd)}/${Number(mm)}` });
});

// Concrete date subline under the nav title. Western digits regardless of locale
// (`-u-nu-latn`, house rule — mirrors SupplierInvoiceSheet / TransactionDetailSheet).
const dateSubline = computed(() => {
  const ymd = collectionYmd(box.value?.collectionDate);
  if (!ymd) return '';
  const [y, m, d] = ymd.split('-').map(Number);
  const parsed = new Date(y, m - 1, d);
  if (Number.isNaN(parsed.getTime())) return '';
  const dateLocale = locale.value === 'ar' ? 'ar-EG-u-nu-latn' : 'en-US';
  return parsed.toLocaleDateString(dateLocale, { weekday: 'long', day: 'numeric', month: 'long' });
});

// ── Summary money ────────────────────────────────────────────────────────────
// Money hero = box.invoiceableCents (converges to the invoice total once
// RECEIVED/CLOSED — same value both captions, design spec Section 5.2). Caption flips on
// lifecycle.
const formattedMoney = computed(() => formatCurrency(Number(box.value?.invoiceableCents) || 0));
const moneyCaptionText = computed(() =>
  t(`supplier.dailyOrders.box.moneyCaption.${moneyCaptionKey.value}`),
);

const invoiceAria = computed(() =>
  t('supplier.dailyOrders.invoice.openAria', { title: dayTitle.value }),
);

// ── View state ───────────────────────────────────────────────────────────────
const hasItems = computed(() => items.value.length > 0);
const errorState = computed(() => !!error.value && !hasItems.value);
const firstLoadSkeleton = computed(() => loading.value && !hasItems.value && !error.value);
const emptyState = computed(() => !loading.value && !error.value && !hasItems.value);

// ── Item groups ──────────────────────────────────────────────────────────────
// Open day → one heading-less list. Finished day → what the collector took
// (`collected` = the plan outcome, not the item status that waits for the
// leader's check), then what he did not; an empty group is dropped.
const itemGroups = computed(() => {
  if (!isClosed.value) return [{ key: 'all', items: items.value }];
  const taken = items.value.filter((item) => item.collected);
  const notTaken = items.value.filter((item) => !item.collected);
  return [
    { key: 'taken', items: taken },
    { key: 'notTaken', items: notTaken },
  ]
    .filter((group) => group.items.length > 0)
    .map((group) => ({
      ...group,
      heading: t(`supplier.dailyOrders.box.sections.${group.key}`, { n: group.items.length }),
    }));
});

// ── Sheets ───────────────────────────────────────────────────────────────────
const sourceSheetOpen = ref(false);
const activeSourceItem = ref(null);
const invoiceSheetOpen = ref(false);

function onViewPost(item) {
  activeSourceItem.value = item;
  sourceSheetOpen.value = true;
}

function openInvoice() {
  invoiceSheetOpen.value = true;
}

// ── Refresh / retry ──────────────────────────────────────────────────────────
// F7 passes a `done` callback to @ptr:refresh; the composable's refresh(done)
// invokes it in its finally.
function onPtrRefresh(done) {
  refresh(done);
}

function onRetry() {
  loadInitial();
}

// ── Teardown — belt-and-braces over onScopeDispose (composable H1 caveat). ────
function onPageBeforeRemove() {
  dispose();
}

onMounted(() => {
  loadInitial();
});
</script>

<style scoped>
.supplier-box-detail-page {
  --f7-page-bg-color: var(--poolit-bg-secondary, #f8f9fa);
}

.dark .supplier-box-detail-page {
  --f7-page-bg-color: var(--f7-color-gray-900, #111827);
}

/* Navbar back-link — 44px tap target (WCAG 2.5.5) */
.supplier-box-detail-page :deep(.navbar-inner .left .link) {
  min-height: 44px;
  display: flex;
  align-items: center;
}

/* ── Per-order summary bar — fixed bar pinned below the navbar, OUTSIDE the scroll.
   `position:absolute` (not a normal-flow `flex-shrink:0` sibling — `.page` is not a flex
   container, so that would clip the last item); `top` tracks the navbar via
   --f7-page-navbar-offset. Height reserved as --f7-page-content-extra-padding-top by
   useFixedSummaryBar. Byte-identical to the list page's .daily-orders-overview
   (see header comment; P5 2026-07-07). ──────────────────────────────────────────── */
.sbd-summary {
  position: absolute;
  top: var(--f7-page-navbar-offset, 0px);
  left: 0;
  right: 0;
  z-index: 20;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 16px 13px;
  background: var(--poolit-bg-primary, #fff);
  border-bottom: 1px solid var(--poolit-border-color, #e2e8f0);
}

.dark .sbd-summary {
  background: var(--f7-color-gray-800, #1f2937);
  border-bottom-color: var(--f7-color-gray-700, #374151);
}

.sbd-summary__money {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.sbd-summary__amt {
  font-size: 22px;
  font-weight: var(--poolit-font-weight-bold, 800);
  color: var(--poolit-primary-900, #31006f);
  font-variant-numeric: tabular-nums;
  line-height: 1.1;
}

.dark .sbd-summary__amt {
  color: var(--poolit-primary-100, #e6dcff);
}

.sbd-summary__cap {
  font-size: 11.5px;
  color: var(--poolit-text-muted, #6b7280);
  line-height: 1.3;
}

/* ── Leading invoice card ───────────────────────────────────────────────────── */
.sbd-invoice-card {
  display: flex;
  align-items: center;
  gap: 12px;
  width: calc(100% - 32px);
  margin: 12px 16px 4px;
  padding: 14px 16px;
  min-height: 44px;
  border: 0;
  border-radius: var(--poolit-radius-lg, 16px);
  background: var(--poolit-primary-50, #f5f0ff);
  color: var(--poolit-primary-700, #6621e2);
  font-family: inherit;
  text-align: start;
  cursor: pointer;
}

.dark .sbd-invoice-card {
  background: var(--f7-color-gray-800, #1f2937);
  color: var(--poolit-primary-200, #c9b4ff);
}

.sbd-invoice-card__icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sbd-invoice-card__label {
  flex: 1;
  min-width: 0;
  font-size: 15px;
  font-weight: var(--poolit-font-weight-semibold, 600);
}

.sbd-invoice-card__cta {
  flex-shrink: 0;
  font-size: 13px;
  font-weight: var(--poolit-font-weight-semibold, 600);
}

/* ── States ─────────────────────────────────────────────────────────────────── */
.sbd-empty,
.sbd-error {
  padding-top: var(--poolit-spacing-xl, 32px);
  text-align: center;
}

.sbd-empty h3 {
  margin: 12px 0 4px;
  font-size: 17px;
  color: var(--poolit-text-headline, var(--f7-text-color));
}

/* text-muted (4.83:1), NOT text-placeholder (2.54:1, fails). */
.sbd-empty p,
.sbd-error p {
  color: var(--poolit-text-muted, #6b7280);
  font-size: 14px;
}

.sbd-error__retry {
  margin-top: var(--poolit-spacing-md, 16px);
}

/* ── Finished-day group heading (taken / not taken) ─────────────────────────── */
.sbd-group-heading {
  margin: 18px 16px 2px;
  font-size: 13px;
  font-weight: var(--poolit-font-weight-semibold, 600);
  color: var(--poolit-text-secondary, #374151);
  font-variant-numeric: tabular-nums;
}

.dark .sbd-group-heading {
  color: var(--f7-color-gray-300, #d1d5db);
}

/* ── First-load skeleton (item-card-shaped) ────────────────────────────────── */
.sbd-skeleton {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px 16px;
}

.sbd-skel-card {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: var(--poolit-spacing-md, 16px);
  border-radius: var(--poolit-radius-lg, 16px);
  background: var(--poolit-bg-primary, #fff);
}

.dark .sbd-skel-card {
  background: var(--f7-color-gray-800, #1f2937);
}

.sbd-skel-card__line {
  height: 14px;
  border-radius: var(--poolit-radius-sm, 4px);
}

.sbd-skel-card__line--title {
  width: 55%;
}

.sbd-skel-card__line--sub {
  width: 35%;
}

.sbd-skel-card__line--actions {
  width: 100%;
  height: 44px;
}
</style>
